<template>
  <el-form class="summary" ref="form" label-position="top">
    <h1>
      S.H.A.P.E. Profile Results
      &nbsp;
      <el-button @click="print">Print</el-button>
    </h1>

    <div v-if="missingCampus">
      Please
      <router-link to="/campus">choose your campus here</router-link>
      to view your results.
    </div>
    <div v-else-if="missingInfo">
      Please
      <router-link to="/info">enter your information here</router-link>
      to view your results.
    </div>
    <div v-else-if="missingGifts">
      Please
      <router-link to="/gifts">fill out your gifts here</router-link>
      to view your results.
    </div>
    <div v-else-if="missingHeart">
      Please
      <router-link to="/heart">specify your passions here</router-link>
      to view your results.
    </div>
    <div v-else-if="missingAbilities">
      Please
      <router-link to="/abilities">specify your abilities here</router-link>
      to view your results.
    </div>
    <div v-else-if="missingPersonality">
      Please
      <router-link to="/personality">specify your personality traits here</router-link>
      to view your results.
    </div>
    <div v-else>
      <h2 class="mb0">Spiritual Gifts</h2>
      <p>Your top 6 Gifts/Gifts Mix: (You may have scored the same for several Gifts)</p>
      <p v-if="!topGifts.length">Not provided</p>
      <div v-else>
        <div class="gifts">
          <div class="gift" v-for="gift, index in topGifts" :key="gift.name">
            <div class="gift__number">{{ index + 1 }}</div>
            <div class="gift__info">
              <div class="gift__name">{{ gift.name }}</div>
              <div class="gift__description">{{ gift.description }}</div>
            </div>
          </div>
        </div>

        <p>
          God has gifted you with an expression of His Holy Spirit to support
          His vision and mission of the church. It is a worldwide vision to reach all people with the gospel
          of Christ. Commit to discovering and developing your spiritual gifts.
        </p>
      </div>

      <hr>

      <el-row :gutter="20">
        <el-col :md="12">
          <h2 class="mb0">Heart</h2>
          <p>The people you would like to serve most:</p>
          <p v-if="!passions.people.length">Not provided</p>
          <ul v-else>
            <li v-for="passion in passions.people" :key="passion">
              {{ passion === 'Other' ? getOtherPassion('people') : passion }}
            </li>
          </ul>
        </el-col>
        <el-col :md="12">
          <h2 class="mb0">Issues and Causes</h2>
          <p>The issues and causes you feel most strongly about:</p>
          <p v-if="!passions.issues.length">Not provided</p>
          <ul v-else>
            <li v-for="passion in passions.issues" :key="passion">
              {{ passion === 'Other' ? getOtherPassion('issues') : passion }}
            </li>
          </ul>
        </el-col>
      </el-row>

      <hr>

      <h2>Abilities</h2>
      <div class="summary-abilities">
        <p v-if="!numAbilities.length">Not provided</p>
        <div v-else class="group" v-for="abilities, group in groupedAbilities" :key="group">
          <div class="group__name">
            {{ group }}
          </div>
          <ul class="group__abilities">
            <li v-for="ability in abilities" :key="ability">{{ ability }}</li>
          </ul>
        </div>
      </div>

      <hr>

      <h2>Personality</h2>
      <p>
        You are <b>{{ taskOrientedPersonality ? 'Task-Oriented' : 'People Oriented' }}</b>:
      </p>

      <div v-if="taskOrientedPersonality">
        <p>
          You tend to focus on the tasks that need to be performed in order to meet certain goals or to achieve
          a certain performance standard. When working in teams, you are valuable because you help ensure
          deadlines are met and jobs are completed.
        </p>
        <p>
          Task-oriented leaders can break down a project into its component parts, create project timelines,
          and ensure that milestones are met. They are typically less concerned with the idea of catering to
          team needs and more concerned with finding the step-by-step solution required to meet specific goals.
          They will often actively define the work and the roles required, put structures in place, and monitor
          progress within the team.
        </p>
      </div>
      <div v-else>
        <p>
          You tend to focus on the satisfaction, motivation and general well-being of team members.
          When working in teams, you are valuable because you help to encourage good teamwork and
          collaboration through fostering positive relationships and good communication.
        </p>
        <p>
          People-oriented leaders want to build a positive environment where individuals feel driven.
          They work to resolve personal conflicts and address dissatisfaction with a job.
          They will often put people first to ensure that such problems stay at a minimum and productivity is high.
        </p>
      </div>

      <p>&nbsp;</p>

      <p>
        You are <b>{{ structuredPersonality ? 'Structured' : 'Unstructured' }}</b>:
      </p>

      <p v-if="structuredPersonality">
        You tend to need clarity and organization when working on a project.
        You prefer to make a plan and stick to it.
      </p>
      <p v-else>
        You tend to be energetic, adaptable, very flexible and creative.
        You love trying out new things.
      </p>

      <hr>

      <h2>Experience</h2>

      <h3>Spiritual experiences</h3>
      <p v-if="!experience.spiritual">
        Not provided.
      </p>
      <template v-else>
        <nl2br tag="p" :text="experience.spiritual" />
      </template>

      <h3>Painful experiences</h3>
      <p v-if="!experience.painful">
        Not provided.
      </p>
      <template v-else>
        <nl2br tag="p" :text="experience.painful" />
      </template>

      <h3>Educational experiences</h3>
      <p v-if="!experience.educational">
        Not provided.
      </p>
      <template v-else>
        <nl2br tag="p" :text="experience.educational" />
      </template>

      <h3>Ministry experience</h3>
      <p v-if="!experience.ministry">
        Not provided.
      </p>
      <template v-else>
        <nl2br tag="p" :text="experience.ministry" />
      </template>

      <p>&nbsp;</p>

      <ui-form-actions class="results-actions">
        <el-button @click="goBack">Back</el-button>
        <el-button type="primary" @click="print">Print</el-button>
        <el-button class="reset-button" type="danger" @click="reset">Reset</el-button>
      </ui-form-actions>
    </div>
  </el-form>
</template>

<script>
  /* eslint-disable max-len */

  import axios from 'axios';
  import each from 'lodash/each';
  import filter from 'lodash/filter';
  import get from 'lodash/get';
  import keys from 'lodash/keys';
  import map from 'lodash/map';
  import slice from 'lodash/slice';
  import sortBy from 'lodash/sortBy';
  import sum from 'lodash/sum';
  import swal from 'sweetalert2';
  import values from 'lodash/values';
  import step from '../mixins/step';

  export default {
    mixins: [step],

    name: 'Results',

    methods: {
      submitData() {
        if (this.invalid) {
          return;
        }

        axios.post(this.handlerUrl, {
          id: this.id,
          token: this.token,
          campus: this.$store.state.campus,
          name: this.$store.state.info.name,
          phone: this.$store.state.info.phone,
          email: this.$store.state.info.email,
          preferred: this.$store.state.info.preferred,
          data: this.$store.state,
          meta: {
            numAbilities: this.numAbilities.length,
            topGifts: this.topGifts,
            taskOrientedPersonality: this.taskOrientedPersonality,
            structuredPersonality: this.structuredPersonality,
          },
        }).then((resp) => {
          if ('error' in resp.data) {
            swal('Error', resp.data.error, 'error');
            return;
          }

          this.$store.commit('setServerIdentity', {
            id: resp.data.id,
            token: resp.data.token,
          });
        });
      },

      goBack() {
        this.$router.back();
      },

      print() {
        window.print();
      },

      reset() {
        swal({
          title: 'Are you sure?',
          text: 'This will clear out your responses.',
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, reset!',
        }).then((result) => {
          if (result.value) {
            swal(
              'Done!',
              'Your responses were reset.',
              'success',
            ).catch(swal.noop);

            this.$store.commit('reset');
            this.$router.push('/gifts');
          }
        }).catch(swal.noop);
      },

      getOtherPassion(group) {
        return get(this.otherPassions, group, 'N/A');
      },
    },

    computed: {
      invalid() {
        return this.missingInfo || this.missingGifts || this.missingHeart || this.missingAbilities || this.missingPersonality;
      },

      missingCampus() {
        return this.$store.state.campus === null;
      },

      missingInfo() {
        const info = this.$store.state.info;

        if (info.name === '' || info.email === '' || info.phone === '') {
          return true;
        }

        return false;
      },

      missingGifts() {
        return this.gifts.filter(gift => gift.answer === null).length > 0;
      },

      missingHeart() {
        return this.passions.people.length === 0 || this.passions.issues.length === 0;
      },

      missingAbilities() {
        return keys(this.groupedAbilities).length === 0 ||
          filter(this.groupedAbilities, abilities => abilities.length === 0).length > 0;
      },

      missingPersonality() {
        return values(this.personality).filter(value => value === null).length > 0;
      },

      gifts() {
        return this.$store.state.gifts;
      },

      passions() {
        return this.$store.state.passions;
      },

      otherPassions() {
        return this.$store.state.passionsOther;
      },

      groupedAbilities() {
        return this.$store.state.abilities;
      },

      numAbilities() {
        return sum(values(this.groupedAbilities));
      },

      personality() {
        return this.$store.state.personality;
      },

      experience() {
        return this.$store.state.experience;
      },

      taskOrientedPersonality() {
        let score = 0;

        score += this.personality.comfortable;
        score += this.personality.task;
        score += this.personality.excited;
        score += this.personality.accomplished;
        score += this.personality.meeting;
        score += this.personality.concerned;
        score += this.personality.value;

        return score <= 17;
      },

      structuredPersonality() {
        let score = 0;

        score += this.personality.vacation;
        score += this.personality.guidelines;
        score += this.personality.projects;
        score += this.personality.like;
        score += this.personality.routine;
        score += this.personality.tasks;

        return score >= 18;
      },

      giftAnswerKey() {
        return [
          {
            name: 'Administration',
            questions: [1, 18, 35, 52, 69, 86, 103],
            description: `
              Persons with the gift of administration lead the body by steering others to remain on task.
              Administration enables the body to organize according to God-given purposes and long-term goals
              (1 Cor. 12:28).
            `,
          },
          {
            name: 'Apostleship',
            questions: [2, 19, 36, 53, 70, 87, 104],
            description: `
              The church sends apostles from the body to plant churches or be missionaries. Apostles motivate
              the body to look beyond its walls in order to carry out the Great Commission (1 Cor. 12:28; Eph. 4:11).
            `,
          },
          {
            name: 'Discernment',
            questions: [3, 20, 37, 54, 71, 88, 105],
            description: `
              Discernment aids the body by recognizing the true intentions of those within or related to the body.
              Discernment tests the message and actions of others for the protection and well-being of the
              body (1 Cor. 12:10).
            `,
          },
          {
            name: 'Encouragement',
            questions: [4, 21, 38, 55, 72, 89, 106],
            description: `
              Possessors of this gift encourage members to be involved in and enthusiastic about the work of the Lord.
              Members with this gift are good counselors and motivate others to service. Exhortation exhibits itself
              in preaching, teaching, and ministry (Rom. 12:8).
            `,
          },
          {
            name: 'Evangelism',
            questions: [5, 22, 39, 56, 73, 90, 107],
            description: `
              God gifts his church with evangelists to lead others to Christ effectively and enthusiastically.
              This gift builds up the body by adding new members to its fellowship (Eph. 4:11).
            `,
          },
          {
            name: 'Faith',
            questions: [6, 23, 40, 57, 74, 91, 108],
            description: `
              Faith trusts God to work beyond the human capabilities of the people. Believers with this gift
              encourage others to trust in God in the face of apparently insurmountable odds (1 Cor.12:9).
            `,
          },
          {
            name: 'Giving',
            questions: [7, 24, 41, 58, 75, 92, 109],
            description: `
              Members with the gift of giving give freely and joyfully to the work and mission of the body.
              Cheerfulness and liberality are characteristics of individuals with this gift (Rom. 12:8).
            `,
          },
          {
            name: 'Helps',
            questions: [8, 25, 42, 59, 76, 93, 110],
            description: `
              Those with the gift of service/helps recognize practical needs in the body and joyfully give
              assistance to meeting those needs. Christians with this gift do not mind working behind the
              scenes (1 Cor. 12:28; Rom. 12:7).
            `,
          },
          {
            name: 'Hospitality',
            questions: [9, 26, 43, 60, 77, 94, 111],
            description: `
              Those with this gift have the ability to make visitors, guests, and strangers feel at ease.
              They often use their home to entertain guests. Persons with this gift integrate new members
              into the body (1 Pet. 4:9).
            `,
          },
          {
            name: 'Intercession',
            questions: [10, 27, 44, 61, 78, 95, 112],
            description: `
              The Spirit enables people with this gift to pray for extended periods of time.
              They are empowered to pray faithfully for others and to identify specific responses
              to those prayers (Col. 1:9,10).
            `,
          },
          {
            name: 'Knowledge',
            questions: [11, 28, 45, 62, 79, 96, 113],
            description: `
              The gift of knowledge manifests itself in teaching and training in discipleship. It is the
              God-given ability to learn, know, and explain the precious truths of God’s Word. A word of
              knowledge is a Spirit-revealed truth (1 Cor. 12:28).
            `,
          },
          {
            name: 'Leadership',
            questions: [12, 29, 46, 63, 80, 97, 114],
            description: `
              Leadership aids the body by leading and directing members to accomplish the goals and purposes of
              the church. Leadership motivates people to work together in unity toward common goals (Rom. 12:8).
            `,
          },
          {
            name: 'Mercy',
            questions: [13, 30, 47, 64, 81, 98, 115],
            description: `
              Cheerful acts of compassion characterize those with the gift of mercy. Persons with this gift aid
              the body by empathizing with hurting members. They keep the body healthy and unified by keeping
              others aware of the needs within the church (Rom. 12:8).
            `,
          },
          {
            name: 'Prophecy',
            questions: [14, 31, 48, 65, 82, 99, 116],
            description: `
              The gift of prophecy is proclaiming the Word of God boldly. This builds up the body and leads to
              conviction of sin. Prophecy manifests itself in preaching and teaching (1 Cor. 12:10; Rom. 12:6).
            `,
          },
          {
            name: 'Shepherding',
            questions: [15, 32, 49, 66, 83, 100, 117],
            description: `
              The gift of shepherding is manifested in persons who look out for the spiritual welfare of others.
              Although pastors, like shepherds, do care for members of the church, this gift is not limited to a
              pastor or staff member (Eph. 4:11).
            `,
          },
          {
            name: 'Teaching',
            questions: [16, 33, 50, 67, 84, 101, 118],
            description: `
              Teaching is instructing members in the truths and doctrines of God’s Word for the purposes of
              building up, unifying, and maturing the body (1 Cor. 12:28; Rom. 12:7; Eph. 4:11).
            `,
          },
          {
            name: 'Wisdom',
            questions: [17, 34, 51, 68, 85, 102, 119],
            description: `
              Wisdom is the gift that discerns the work of the Holy Spirit in the body and applies His teachings
              and actions to the needs of the body (1 Cor. 12:28).
            `,
          },
        ];
      },

      giftScores() {
        return map(this.giftAnswerKey, (key) => {
          let totalScore = 0;
          each(key.questions, (questionNumber) => {
            const score = this.gifts[questionNumber - 1].answer;
            if (score) {
              totalScore += parseInt(score, 10);
            }
          });

          return {
            name: key.name,
            score: totalScore,
            description: key.description,
          };
        });
      },

      topGifts() {
        return slice(
          sortBy(
            filter(this.giftScores, gift => gift.score > 0),
            gift => gift.score,
          ).reverse(),
          0,
          6,
        );
      },

      id() {
        return this.$store.state.id;
      },

      token() {
        return this.$store.state.token;
      },
    },

    created() {
      this.handlerUrl = 'https://zionlandover.com/shape-handler';
    },

    mounted() {
      this.submitData();
    },
  };
</script>

<style lang="scss">
  @import "~styles/vars";
  @import "~element-theme/mixins/mixins.scss";

  hr {
    background-color: $--border-color-base;
    border: none;
    display: block;
    height: 1px;
    margin: 2rem 0 0;
  }

  .summary {
    ul {
      margin: 0;
      padding: 0 0 0 1.2rem;
    }
    li {
      margin: .35rem 0;
      padding: 0;
    }
  }

  .gifts {
    display: flex;
    flex-wrap: wrap;
  }

  .gift {
    align-items: flex-start;
    display: flex;
    flex-basis: 50%;
    margin: 1rem 0;
    padding-right: 2rem;
    &__number {
      border: 1px solid $--border-color-base;
      border-radius: 50em;
      flex-shrink: 0;
      font-size: 1.25rem;
      height: 2em;
      line-height: .3;
      margin-right: 1rem;
      padding: 1rem 0;
      text-align: center;
      width: 2em;
    }
    &__name {
      font-size: 1.25rem;
      margin-bottom: .5rem;
    }
    &__description {
      color: $--color-text-secondary;
      font-size: .85rem;
    }
  }

  .summary-abilities {
    display: flex;
    flex-wrap: wrap;

    .group {
      flex-basis: 100%;
      margin-bottom: 2rem;
      @media print {
        flex-basis: 33.33%;
      }
      @include res(md) {
        flex-basis: 50%;
      }
      @include res(lg) {
        flex-basis: 33.33%;
      }

      &__name {
        font-weight: 600;
        margin-bottom: .25rem;
      }
    }
  }

  .results-actions {
    display: flex;
    .reset-button {
      margin-left: auto;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/Results.vue